// embla-init.ts
import type { EmblaCarouselType } from 'embla-carousel'; // Import the official type

// Remove your custom EmblaApi interface - it's replaced by EmblaCarouselType

/**
 * Initialize the Embla carousel by wiring up navigation buttons and dot controls.
 * @param event - A custom event containing the Embla API instance.
 */
// Change CustomEvent<EmblaApi> to CustomEvent<EmblaCarouselType>
// Remove 'async' and ': Promise<void>'
export function init(event: CustomEvent<EmblaCarouselType>): void {
  // event.detail is now correctly typed as EmblaCarouselType
  const emblaApi = event.detail;
  const rootNode = emblaApi.rootNode(); // Method exists on EmblaCarouselType

  const prevButton = rootNode.querySelector<HTMLButtonElement>('.embla__prev');
  if (prevButton) {
    // scrollPrev exists on EmblaCarouselType
    const handlePrevClick = () => emblaApi.scrollPrev();
    prevButton.addEventListener('click', handlePrevClick, false);
    // Add logic to disable/enable buttons based on emblaApi.canScrollPrev()
    // See example in previous answers or Embla docs
  }

  const nextButton = rootNode.querySelector<HTMLButtonElement>('.embla__next');
  if (nextButton) {
    // scrollNext exists on EmblaCarouselType
    const handleNextClick = () => emblaApi.scrollNext();
    nextButton.addEventListener('click', handleNextClick, false);
    // Add logic to disable/enable buttons based on emblaApi.canScrollNext()
    // See example in previous answers or Embla docs
  }

  const dotsContainer = rootNode.querySelector<HTMLElement>('.control-dots .embla__dots');
  if (dotsContainer) {
    // Pass the correctly typed emblaApi to the helper function
    const cleanupDots = addDotButtonsWithHandlers(emblaApi, dotsContainer);
    // 'destroy' event exists on EmblaCarouselType
    emblaApi.on('destroy', cleanupDots);
  }

  // Add button enable/disable logic update on 'select' and 'reInit' events here
  // (This was missing from your original init but is good practice)
  const updateButtonStates = () => {
    if (prevButton) {
      prevButton.disabled = !emblaApi.canScrollPrev();
    }
    if (nextButton) {
      nextButton.disabled = !emblaApi.canScrollNext();
    }
  };

  emblaApi.on('select', updateButtonStates);
  emblaApi.on('reInit', updateButtonStates);
  updateButtonStates(); // Initial check
}

/**
 * Adds dot buttons for each scroll snap and wires up click handlers.
 * @param emblaApi - Embla carousel API (now correctly typed).
 * @param container - Container where dot buttons should be rendered.
 * @returns A function to remove all dot buttons and their event listeners.
 */
// Change emblaApi parameter type from EmblaApi to EmblaCarouselType
export function addDotButtonsWithHandlers(emblaApi: EmblaCarouselType, container: HTMLElement): () => void {
  // scrollSnapList exists on EmblaCarouselType
  const scrollSnaps = emblaApi.scrollSnapList();
  let dotButtons: HTMLButtonElement[] = []; // Keep track of buttons for cleanup

  // Function to create or update dots
  const setupDots = () => {
    // Clear existing dots and listeners if re-initializing
    container.innerHTML = '';
    dotButtons = []; // Clear the array

    scrollSnaps.forEach((_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.classList.add('embla__dot');
      button.setAttribute('aria-label', `Slide ${index + 1}`);
      container.appendChild(button);
      // scrollTo exists on EmblaCarouselType
      button.addEventListener('click', () => emblaApi.scrollTo(index), false);
      dotButtons.push(button);
    });
  };

  // Function to update the active dot
  const updateActiveDot = () => {
    // previousScrollSnap and selectedScrollSnap exist on EmblaCarouselType
    const previous = emblaApi.previousScrollSnap();
    const selected = emblaApi.selectedScrollSnap();
    if (dotButtons[previous]) {
      dotButtons[previous].classList.remove('embla__dot--selected');
    }
    if (dotButtons[selected]) {
      dotButtons[selected].classList.add('embla__dot--selected');
    }
  };

  // Initial setup
  setupDots();
  updateActiveDot();

  // Listen for events - 'on' method exists on EmblaCarouselType
  emblaApi.on('select', updateActiveDot);
  // Re-setup dots on reInit as the number of snaps might change
  emblaApi.on('reInit', () => {
    setupDots();
    updateActiveDot();
  });

  // Return a cleanup function (this part was mostly correct, just simplified setup)
  return () => {
    container.innerHTML = ''; // Simple cleanup by removing all children
  };
}
